import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FormError } from '@/components/form';
import { AuthShell } from '@/features/auth/auth-shell';
import { SignOutLink } from '@/features/auth/sign-out-link';
import { api, ApiError, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/auth';

export const Route = createFileRoute('/verify')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' && s.redirect.startsWith('/') ? s.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: '/login', search });
    if (me.mfaVerified) throw redirect({ to: '/' });
    if (!me.mfaEnrolled) throw redirect({ to: '/setup-2fa', search });
  },
  component: VerifyPage,
});

function VerifyPage() {
  const { redirect: next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);

  const verify = useMutation({
    mutationFn: () => api.post('/v1/auth/mfa/verify', { code }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['me'] });
      await navigate({ to: next ?? '/' });
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.code === 'LOCKED') {
        queryClient.setQueryData(['me'], null);
      }
      setCode('');
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    verify.mutate();
  }

  const locked = verify.error instanceof ApiError && verify.error.code === 'LOCKED';

  return (
    <AuthShell
      title="Two-step verification"
      description={
        recovery
          ? 'Enter one of the recovery codes you saved when you set up your account. Each code works once.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
      footer={<SignOutLink label="Sign in as someone else" />}
    >
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <FormError message={verify.isError ? (locked ? 'Too many attempts. Your account is locked for a few minutes; sign in again later.' : errorMessage(verify.error)) : null} />
        {recovery ? (
          <Field id="code" label="Recovery code">
            <Input id="code" autoComplete="off" autoFocus placeholder="XXXXX-XXXXX" className="font-mono uppercase" value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
        ) : (
          <Field id="code" label="Authentication code">
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              placeholder="123456"
              className="font-mono text-lg tracking-[0.3em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
        )}
        <Button type="submit" size="lg" disabled={verify.isPending || locked || (recovery ? code.length < 10 : code.length !== 6)}>
          {verify.isPending ? 'Checking…' : 'Verify'}
        </Button>
        <Button
          type="button"
          variant="link"
          className="justify-self-center text-link"
          onClick={() => {
            setRecovery((r) => !r);
            setCode('');
            verify.reset();
          }}
        >
          {recovery ? 'Use my authenticator app instead' : 'I can’t use my authenticator app'}
        </Button>
      </form>
    </AuthShell>
  );
}
